/**
 * Consent Checkbox Validation with Server-Side Consent Logging
 * -------------------------------------------------------------
 * Validates that required consent checkboxes are checked before form submission.
 * Stores consent metadata (timestamp, version) in cart/order attributes for legal compliance.
 * Sends consent records to server API for permanent storage in Neon PostgreSQL.
 * 
 * Compliance: GDPR Art. 7 & 30, CCPA/CPRA, CIPA, CAN-SPAM, TCPA
 * 
 * Consent Data Stored:
 *   - consent_timestamp: ISO 8601 timestamp of when consent was given
 *   - consent_terms_version: Version of terms agreed to (e.g., v2025.01.21)
 *   - consent_type: Type of consent (checkout_terms_privacy, register, newsletter)
 *   - consent_method: How consent was given (checkbox_click)
 * 
 * Server Logging:
 *   - Sends consent record to /api/consent/log endpoint
 *   - Stored in em_consent_logs table in Neon PostgreSQL
 *   - Captures IP address, user agent, page URL for audit trail
 */

(function() {
  'use strict';

  // API endpoint for server-side consent logging (hosted on Vercel)
  const CONSENT_API_ENDPOINT = 'https://emc-inventory-sync-webhook.vercel.app/api/consent/log';

  const SELECTORS = {
    consentCheckbox: '.consent-checkbox',
    consentInput: '.consent-checkbox__input[data-consent-required="true"]',
    consentWrapper: '[data-consent-wrapper]',
    checkoutButton: '[data-requires-consent="true"]',
    cartForm: 'form[action*="/cart"]',
    registerForm: 'form[action*="/account"]'
  };

  const MESSAGES = {
    required: 'Please accept the Terms of Use and Privacy Policy to continue.',
    registerRequired: 'Please accept the Terms of Use and Privacy Policy to create your account.'
  };

  /**
   * Generate ISO 8601 timestamp
   */
  function getTimestamp() {
    return new Date().toISOString();
  }

  /**
   * Get Shopify cart token from cookie
   */
  function getCartToken() {
    const match = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Get session ID from Shopify's _shopify_s cookie
   */
  function getSessionId() {
    const match = document.cookie.match(/(?:^|;\s*)_shopify_s=([^;]*)/);
    return match ? decodeURIComponent(match[1]).split('&')[0] : null;
  }

  /**
   * Send consent record to server API for permanent storage
   * @param {Object} consentData - The consent data to log
   * @returns {Promise<Object>} - API response
   */
  async function logConsentToServer(consentData) {
    try {
      const response = await fetch(CONSENT_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Cart-Token': consentData.cart_token || ''
        },
        body: JSON.stringify(consentData)
      });

      const result = await response.json();
      
      if (response.ok) {
        console.log('[Consent] Server logged:', result);
        return { success: true, ...result };
      } else {
        console.warn('[Consent] Server log failed:', result);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('[Consent] API error:', error);
      // Don't block user action on API failure - consent is still captured client-side
      return { success: false, error: error.message };
    }
  }

  /**
   * Update consent metadata fields when checkbox state changes
   */
  function updateConsentMetadata(input, isChecked) {
    const wrapper = input.closest(SELECTORS.consentWrapper);
    if (!wrapper) return;

    const form = input.closest('form');
    const formId = form ? form.id || 'form' : 'form';
    
    // Find the hidden metadata fields
    const timestampField = wrapper.querySelector('[name="attributes[consent_timestamp]"]') ||
                           document.getElementById(formId + '-consent-timestamp');
    const versionField = wrapper.querySelector('[name="attributes[consent_terms_version]"]') ||
                         document.getElementById(formId + '-consent-version');
    const typeField = wrapper.querySelector('[name="attributes[consent_type]"]') ||
                      document.getElementById(formId + '-consent-type');
    const methodField = wrapper.querySelector('[name="attributes[consent_method]"]') ||
                        document.getElementById(formId + '-consent-method');

    if (isChecked) {
      // Get version from data attribute
      const termsVersion = input.dataset.termsVersion || 'v2025.01.21';
      const consentType = input.dataset.consentType || 'checkout_terms_privacy';
      const timestamp = getTimestamp();
      
      // Populate consent metadata
      if (timestampField) timestampField.value = timestamp;
      if (versionField) versionField.value = termsVersion;
      if (typeField) typeField.value = consentType;
      if (methodField) methodField.value = 'checkbox_click';
      
      console.log('[Consent] Recorded:', {
        timestamp: timestampField?.value,
        version: versionField?.value,
        type: typeField?.value,
        method: methodField?.value
      });

      // Get the checkbox label text for audit trail
      const labelElement = wrapper.querySelector('.consent-checkbox__label, label');
      const checkboxLabelText = labelElement ? labelElement.textContent.trim() : null;

      // Log consent to server API (async, non-blocking)
      const consentData = {
        cart_token: getCartToken(),
        session_id: getSessionId(),
        consent_timestamp: timestamp,
        consent_type: consentType === 'checkout_terms_privacy' ? 'terms_and_privacy' : consentType,
        consent_method: 'checkbox',
        terms_version: termsVersion,
        checkbox_label_text: checkboxLabelText,
        page_url: window.location.href,
        referrer_url: document.referrer || null,
        metadata: {
          form_id: formId,
          page_title: document.title,
          viewport: `${window.innerWidth}x${window.innerHeight}`
        }
      };

      // Fire and forget - don't block UI
      logConsentToServer(consentData);
    } else {
      // Clear consent metadata if unchecked
      if (timestampField) timestampField.value = '';
      if (versionField) versionField.value = '';
      if (typeField) typeField.value = '';
      if (methodField) methodField.value = '';
    }
  }

  /**
   * Initialize consent validation on a form
   */
  function initConsentValidation(form) {
    if (!form) return;

    const consentInputs = form.querySelectorAll(SELECTORS.consentInput);
    if (consentInputs.length === 0) return;

    // Add validation on form submit
    form.addEventListener('submit', function(e) {
      const submitButton = e.submitter;
      
      // Only validate if this is a checkout/register submission
      if (submitButton && submitButton.name === 'update') {
        return; // Allow cart updates without consent
      }

      let isValid = true;
      
      consentInputs.forEach(function(input) {
        const label = input.closest(SELECTORS.consentCheckbox);
        
        if (input.required && !input.checked) {
          isValid = false;
          
          // Add error state
          if (label) {
            label.classList.add('has-error');
            
            // Add error message if not exists
            let errorMsg = label.nextElementSibling;
            if (!errorMsg || !errorMsg.classList.contains('consent-error')) {
              errorMsg = document.createElement('p');
              errorMsg.className = 'consent-error';
              errorMsg.textContent = form.action.includes('/account') 
                ? MESSAGES.registerRequired 
                : MESSAGES.required;
              errorMsg.setAttribute('role', 'alert');
              label.parentNode.insertBefore(errorMsg, label.nextSibling);
            }
            errorMsg.style.display = 'block';
          }
          
          // Focus the checkbox
          input.focus();
          input.classList.add('is-invalid');
        } else {
          // Clear error state
          if (label) {
            label.classList.remove('has-error');
            const errorMsg = label.nextElementSibling;
            if (errorMsg && errorMsg.classList.contains('consent-error')) {
              errorMsg.style.display = 'none';
            }
          }
          input.classList.remove('is-invalid');
          
          // Ensure consent metadata is set before form submits
          if (input.checked) {
            updateConsentMetadata(input, true);
          }
        }
      });

      if (!isValid) {
        e.preventDefault();
        e.stopPropagation();
        
        // Scroll to first error
        const firstError = form.querySelector('.consent-checkbox.has-error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });

    // Update consent metadata and clear error state when checkbox is changed
    consentInputs.forEach(function(input) {
      input.addEventListener('change', function() {
        const label = input.closest(SELECTORS.consentCheckbox);
        
        // Update consent metadata
        updateConsentMetadata(input, input.checked);
        
        if (input.checked) {
          input.classList.remove('is-invalid');
          if (label) {
            label.classList.remove('has-error');
            const errorMsg = label.nextElementSibling;
            if (errorMsg && errorMsg.classList.contains('consent-error')) {
              errorMsg.style.display = 'none';
            }
          }
        }
      });
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    // Cart forms
    document.querySelectorAll(SELECTORS.cartForm).forEach(initConsentValidation);
    
    // Registration forms
    document.querySelectorAll(SELECTORS.registerForm).forEach(initConsentValidation);
    
    // Handle dynamically loaded content (e.g., quick cart drawer)
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const forms = node.querySelectorAll 
              ? node.querySelectorAll(SELECTORS.cartForm + ', ' + SELECTORS.registerForm)
              : [];
            forms.forEach(initConsentValidation);
            
            // Also check if the node itself is a form
            if (node.matches && (node.matches(SELECTORS.cartForm) || node.matches(SELECTORS.registerForm))) {
              initConsentValidation(node);
            }
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
